import { connect, disconnect, getConnection, getConnectors, reconnect, watchConnection, watchConnectors,
  type Config, type Connector } from "@wagmi/core";
import type { Eip1193Provider } from "./eip1193";
import type { WalletSessionController } from "./wallet-session";
import { getWalletConfig } from "./wallet-config";

export type WalletOption = Readonly<{ id: string; name: string; icon: string | null; kind: "extension" | "remote" }>;
export type WalletSelectionSnapshot = Readonly<{
  isOpen: boolean; isDetecting: boolean; wallets: readonly WalletOption[];
  pendingId: string | null; error: string | null;
}>;

const priority = (id: string) => id === "io.metamask" ? 0 : id === "com.okex.wallet" ? 1 : 2;

/** Connector state flows one way into the application session, never back into a second connection store. */
export class WalletConnection {
  private disposed = false;
  private generation = 0;
  private detection = 0;
  private synchronization = 0;
  private config: Config | null = null;
  private initializing: Promise<Config> | null = null;
  private pending: Promise<void> | null = null;
  private restoring: Promise<void> | null = null;
  private ignoreConnections = false;
  private unwatch: Array<() => void> = [];
  private providers = new Map<string, Eip1193Provider>();
  private listeners = new Set<() => void>();
  private snapshot: WalletSelectionSnapshot = { isOpen: false, isDetecting: true, wallets: [], pendingId: null, error: null };

  constructor(private readonly session: WalletSessionController,
    private readonly loadConfig: () => Promise<Config> = getWalletConfig) {}

  getSnapshot = (): WalletSelectionSnapshot => this.snapshot;
  subscribe = (listener: () => void) => { this.listeners.add(listener); return () => { this.listeners.delete(listener); }; };
  private update(patch: Partial<WalletSelectionSnapshot>) {
    if (this.disposed) return;
    this.snapshot = { ...this.snapshot, ...patch };
    for (const listener of this.listeners) listener();
  }

  open = (): void => {
    this.update({ isOpen: true, error: null });
    void this.initialize().then(() => this.detect()).catch(error => this.failure(error));
  };
  close = (): void => {
    // Closing the chooser cannot revoke a request already accepted by the wallet.
    this.update({ isOpen: false });
  };

  private initialize(): Promise<Config> {
    if (this.disposed) return Promise.reject(new Error("Wallet connection has been disposed"));
    return this.initializing ??= this.loadConfig().then(config => {
      if (this.disposed) throw new Error("Wallet connection has been disposed");
      this.config = config;
      this.unwatch.push(watchConnectors(config, { onChange: () => { void this.detect().catch(error => this.failure(error)); } }),
        watchConnection(config, { onChange: () => { void this.synchronize().catch(error => this.failure(error)); } }));
      const detect = () => { void this.detect().catch(error => this.failure(error)); };
      window.addEventListener("ethereum#initialized", detect);
      this.unwatch.push(() => window.removeEventListener("ethereum#initialized", detect));
      detect();
      void this.synchronize().catch(error => this.failure(error));
      return config;
    }).catch(error => { this.initializing = null; throw error; });
  }

  private async detect(): Promise<void> {
    const config = this.config;
    if (!config || this.disposed) return;
    const revision = ++this.detection;
    const connectors = [...getConnectors(config)];
    const entries = await Promise.all(connectors.map(async connector => {
      // Listing a remote connector must not initialize a QR session or contact a relay.
      if (connector.type !== "injected") return { connector, provider: null };
      try {
        const provider = await connector.getProvider();
        return isProvider(provider) ? { connector, provider } : null;
      } catch { return null; }
    }));
    if (this.disposed || revision !== this.detection) return;
    const available = entries.filter(entry => entry !== null);
    const namedProviders = new Set(available.filter(entry => entry.connector.id !== "injected").map(entry => entry.provider));
    const seen = new Set<string>();
    const wallets = available.filter(({ connector, provider }) => {
      if (connector.id === "injected" && namedProviders.has(provider)) return false;
      if (seen.has(connector.id)) return false;
      seen.add(connector.id);
      return true;
    }).sort((a, b) => priority(a.connector.id) - priority(b.connector.id)).map(({ connector, provider }): WalletOption => {
      if (provider) this.providers.set(connector.uid, provider);
      return { id: connector.uid, name: connector.id === "injected" ? "Browser wallet" : connector.name,
        icon: safeIcon(connector.icon), kind: connector.type === "injected" ? "extension" : "remote" };
    });
    this.update({ wallets, isDetecting: false });
    this.session.setConnectorAvailability(wallets.length > 0);
  }

  connect = (walletId?: string): Promise<void> => {
    if (this.disposed) return Promise.reject(new Error("Wallet connection has been disposed"));
    if (this.pending) return this.pending;
    this.ignoreConnections = false;
    const generation = ++this.generation;
    this.update({ pendingId: walletId ?? "injected", error: null });
    this.session.setConnectionProgress(true);
    const work = Promise.resolve().then(async () => {
      await this.restoring;
      const config = await this.initialize();
      this.assertCurrent(generation);
      await this.detect();
      this.assertCurrent(generation);
      const selectedId = walletId ?? this.snapshot.wallets.find(wallet => wallet.kind === "extension")?.id;
      const connector = getConnectors(config).find(item => item.uid === selectedId);
      if (!connector) throw new Error("No wallet is available. Install or unlock a browser wallet, then try again.");
      this.update({ pendingId: connector.uid });
      // WalletConnect owns its QR dialog. Release our focus trap before it opens.
      if (connector.type !== "injected") this.close();
      if (getConnection(config).connector?.uid !== connector.uid || getConnection(config).status !== "connected") {
        await connect(config, { connector });
      }
      if (this.disposed || generation !== this.generation) {
        await disconnect(config, { connector });
        await config.storage?.removeItem("recentConnectorId");
      }
      this.assertCurrent(generation);
      await this.synchronize();
      this.assertCurrent(generation);
      if (!this.session.getSnapshot().connected) throw new Error("The wallet disconnected before connection completed. Try again.");
      this.update({ isOpen: false });
    }).catch(error => {
      if (!this.disposed && generation === this.generation) this.failure(error);
      throw error;
    }).finally(() => {
      if (this.pending === work) {
        this.pending = null;
        this.update({ pendingId: null });
        if (!this.disposed && generation === this.generation) this.session.setConnectionProgress(false,
          this.snapshot.error ? new Error(this.snapshot.error) : null);
      }
    });
    this.pending = work;
    return work;
  };

  /** Reconnect only the remembered connector; never probe every installed wallet for authorization. */
  restore = (): Promise<void> => {
    return this.restoring ??= this.restoreConnection().finally(() => { this.restoring = null; });
  };

  private async restoreConnection(): Promise<void> {
    const generation = this.generation;
    const config = await this.initialize();
    if (this.disposed || generation !== this.generation || this.pending) return;
    if (getConnection(config).status !== "disconnected") { await this.synchronize(); return; }
    const id = await config.storage?.getItem("recentConnectorId");
    if (!id || this.disposed || generation !== this.generation || this.pending) return;
    const connector = getConnectors(config).find(item => item.id === id);
    if (!connector) return;
    await reconnect(config, { connectors: [connector] });
    if (this.disposed || this.ignoreConnections) {
      await disconnect(config, { connector });
      return;
    }
    if (!this.disposed && generation === this.generation) await this.synchronize();
  }

  private async synchronize(): Promise<void> {
    const config = this.config;
    if (!config || this.disposed || this.ignoreConnections) return;
    const revision = ++this.synchronization;
    const connection = getConnection(config);
    if (connection.status === "disconnected") { this.session.invalidateConnection(); return; }
    if (connection.status !== "connected") return;
    const { connector, address: account, chainId } = connection;
    let provider = this.providers.get(connector.uid);
    if (!provider) {
      this.session.invalidateConnection();
      const resolved = await connector.getProvider();
      if (this.disposed || revision !== this.synchronization) return;
      if (!isProvider(resolved)) throw new Error("The selected wallet did not provide a compatible connection.");
      provider = resolved;
      this.providers.set(connector.uid, provider);
    }
    if (this.disposed || revision !== this.synchronization) return;
    this.session.syncConnection({ provider, account, chainId, key: connector.uid });
  }

  disconnect = async (): Promise<void> => {
    if (this.disposed) return;
    this.ignoreConnections = true;
    ++this.generation;
    ++this.synchronization;
    const logout = this.session.logout();
    this.session.disconnect();
    this.update({ pendingId: null, isOpen: false, error: null });
    const config = this.config;
    const disconnectWallet = async () => {
      if (config) {
        await config.storage?.removeItem("recentConnectorId");
        await disconnect(config);
      }
    };
    // Observe both promises immediately: connector failure cannot strand logout.
    const results = await Promise.allSettled([logout, disconnectWallet()]);
    for (const result of results) if (result.status === "rejected") throw result.reason;
  };

  private failure(error: unknown): void {
    if (this.disposed) return;
    this.session.setConnectionProgress(false, error);
    this.update({ error: this.session.getSnapshot().error, isDetecting: false });
  }
  private assertCurrent(generation: number) {
    if (this.disposed || generation !== this.generation) throw new Error("Wallet context changed while connecting");
  }
  dispose(): void {
    this.ignoreConnections = true;
    this.disposed = true;
    ++this.generation; ++this.detection; ++this.synchronization;
    for (const unsubscribe of this.unwatch) unsubscribe();
    this.providers.clear();
    this.listeners.clear();
  }
}

function isProvider(value: unknown): value is Eip1193Provider {
  return !!value && typeof value === "object" && "request" in value && typeof value.request === "function";
}

function safeIcon(icon: Connector["icon"]): string | null {
  // Render extension-supplied SVG only as a passive image, never as HTML.
  return icon && /^(data:image\/(?:png|webp|jpeg|svg\+xml)[;,]|https:\/\/)/i.test(icon) ? icon : null;
}
